import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, mediaUrl } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Calendar, Camera, Film, Sparkles, ChevronRight, ChevronDown, BookOpen, Users, Crown, Check, Star, Zap } from "lucide-react";
import Price from "@/components/Price";
import { SEO } from "@/components/SEO";
import {
  organizationSchema, localBusinessSchema, websiteSchema, serviceSchema,
  faqSchema, breadcrumbSchema,
} from "@/lib/seo-schemas";
import { prefetchPackages } from "@/lib/bookingPrefetch";

const HOME_FAQS = [
  { q: "What does Propix3ls offer?", a: "Same-day lifestyle photo shoots, UGC content production, and 4K/8K reels for content creators, influencers, TikTokers, and UGC sellers — plus educational e-books to help you grow as a creator." },
  { q: "Where do you operate?", a: "London (home base) plus scheduled travel weeks in the United States (NYC, LA, Miami) and Canada (Toronto, Vancouver). Specific dates are listed on the booking page." },
  { q: "How fast is same-day delivery?", a: "Raw images are delivered within hours of the shoot ending. Edited photos and 4K reels arrive the same day for the Edit Essentials and Ultimate packages." },
  { q: "Can I book if I'm a UGC creator with no following?", a: "Yes — most of our clients are UGC sellers and emerging creators. The shoot is what builds your portfolio so brands take you seriously." },
  { q: "Do you offer free educational e-books?", a: "Yes. We publish both free and paid e-books covering UGC, brand deals, TikTok strategy, and creator monetization. Browse them at /ebooks (account required)." },
  { q: "What's the cancellation policy?", a: "You can reschedule or cancel from your dashboard up to 24 hours before the shoot." },
];

const FALLBACK_HERO = "https://images.unsplash.com/photo-1736841096703-d042981a6157?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxmYXNoaW9uJTIwbW9kZWwlMjBuZW9uJTIwbGlnaHRpbmclMjBmdXR1cmlzdGljJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzczNjAyODcwfDA&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  const cachedSite = (() => {
    try { return JSON.parse(localStorage.getItem("propix3ls_site_content_cache") || "null"); }
    catch { return null; }
  })();
  const [travel, setTravel] = useState([]);
  const [heroImage, setHeroImage] = useState(cachedSite?.hero_image || null);
  const [categories, setCategories] = useState(
    cachedSite?.categories
      ? [...cachedSite.categories].sort((a, b) => a.order - b.order).slice(0, 4)
      : []
  );
  const [ebooks, setEbooks] = useState([]);
  const [communityTiers, setCommunityTiers] = useState([]);
  const [expandedTierId, setExpandedTierId] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    axios.get(`${API}/travel`).then(r => setTravel(r.data)).catch(() => {});
    axios.get(`${API}/site-content`).then(r => {
      setHeroImage(r.data.hero_image || FALLBACK_HERO);
      if (r.data.categories) {
        const sorted = [...r.data.categories].sort((a, b) => a.order - b.order);
        setCategories(sorted.slice(0, 4));
      }
      try { localStorage.setItem("propix3ls_site_content_cache", JSON.stringify(r.data)); } catch (_e) { /* storage full / disabled */ }
    }).catch(() => {
      if (!cachedSite?.hero_image) setHeroImage(FALLBACK_HERO);
    });
    axios.get(`${API}/ebooks`).then(r => setEbooks(r.data.filter(e => e.status === "published"))).catch(() => {});
    axios.get(`${API}/community/tiers`).then(r => setCommunityTiers(r.data.filter(t => t.active).sort((a, b) => a.order - b.order))).catch(() => {});

    const idle = setTimeout(() => prefetchPackages(), 800);
    return () => clearTimeout(idle);
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Lifestyle Content for Creators, Influencers & UGC | London · USA · Canada"
        description="Same-day lifestyle photo shoots, UGC content, and 4K reels for content creators, influencers, and TikTokers. Book in London, NYC, LA, Toronto. Educational e-books to grow your creator brand."
        keywords="lifestyle content creator, ugc content shoot, influencer photographer london, content creator photographer nyc, tiktok content shoot, same-day photo reels, ugc creator e-books, influencer education"
        path="/"
        schemas={[
          organizationSchema(),
          websiteSchema(),
          localBusinessSchema({ region: "London", country: "UK", areaName: "London, UK" }),
          serviceSchema({
            name: "Same-Day Lifestyle Photo & Reels for Creators",
            description: "Same-day lifestyle photo, UGC content, and 4K reels for content creators, influencers, TikTokers and UGC sellers across the UK, USA, and Canada.",
            priceFrom: 80,
            currency: "GBP",
          }),
          faqSchema(HOME_FAQS),
          breadcrumbSchema([{ name: "Home", url: "https://propix3ls.com/" }]),
        ]}
      />

      {/* ─── Hero Section ─── */}
      <section className="relative min-h-screen flex items-end" data-testid="hero-section">
        <div className="absolute inset-0">
          {heroImage ? (
            <img src={mediaUrl(heroImage)} alt="Propix3ls Hero" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-[#050505]" />
          )}
          {/* 3-layer gradient: bottom-up dark */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
          {/* left-to-right fade */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/90 via-[#050505]/40 to-transparent" />
          {/* radial vignette */}
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 40%, #050505 100%)' }} />
          {/* grain texture overlay */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.04]"
            style={{
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pb-32 md:pb-44 w-full">
          <div className="max-w-2xl">
            {/* thin line accent + subtitle */}
            <div className="flex items-center gap-3 mb-5 animate-fade-in-up stagger-1">
              <span className="block w-10 h-px bg-[#D946EF]" />
              <p className="font-['JetBrains_Mono'] text-[#D946EF] text-xs md:text-sm tracking-widest uppercase">
                Photography &bull; Video Reels &bull; Worldwide
              </p>
            </div>
            <h1 className="font-['Syne'] text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mb-7 animate-fade-in-up stagger-2">
              <span className="block text-white font-normal">Visuals</span>
              <span className="block font-extrabold text-white mt-1">That <span className="gradient-text">Pulse</span>.</span>
            </h1>
            <p className="text-zinc-400 font-['Manrope'] text-base md:text-lg max-w-md mb-10 animate-fade-in-up stagger-3">
              Cinematic photography and social media reels crafted for creators who demand more.
            </p>
            <div className="flex flex-wrap gap-3 animate-fade-in-up stagger-4">
              <Link to="/booking" onMouseEnter={() => prefetchPackages()} onTouchStart={() => prefetchPackages()} onFocus={() => prefetchPackages()}>
                <Button className="bg-[#D946EF] hover:bg-[#C026D3] text-white font-['Manrope'] font-semibold px-6 py-3 rounded-full text-sm" data-testid="hero-book-btn">
                  Book a Shoot <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button variant="ghost" className="text-white hover:bg-white/10 font-['Manrope'] font-semibold px-6 py-3 rounded-full text-sm border border-zinc-700" data-testid="hero-portfolio-btn">
                  View Portfolio
                </Button>
              </Link>
            </div>
          </div>
        </div>
        {/* Floating scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 animate-fade-in stagger-5">
          <span className="font-['JetBrains_Mono'] text-[9px] tracking-[0.3em] uppercase text-zinc-500">Scroll</span>
          <ChevronDown className="w-4 h-4 text-zinc-500 animate-bounce" />
        </div>
      </section>

      {/* ─── Section Divider ─── */}
      <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(217,70,239,0.3), transparent)' }} />

      {/* ─── Services Strip ─── */}
      <section className="py-8 md:py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-4 md:gap-3">
            {[
              { icon: Camera, text: "Fashion Photography" },
              { icon: Film, text: "Cinematic Reels" },
              { icon: Sparkles, text: "Professional Edits" },
              { icon: MapPin, text: "International Shoots" },
            ].map((item, i, arr) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm hover:border-[#D946EF]/30 hover:shadow-[0_0_16px_rgba(217,70,239,0.1)] transition-all duration-300 cursor-default">
                  <item.icon className="w-4 h-4 text-[#D946EF]" />
                  <span className="font-['Manrope'] text-sm font-medium text-zinc-400">{item.text}</span>
                </div>
                {/* Separator dot on desktop */}
                {i < arr.length - 1 && (
                  <span className="hidden md:block w-1 h-1 rounded-full bg-zinc-700 ml-2" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section Divider ─── */}
      <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(217,70,239,0.3), transparent)' }} />

      {/* ─── Portfolio Preview ─── */}
      <section className="py-20 md:py-32 px-4 md:px-8" data-testid="portfolio-preview-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10 md:mb-14">
            <div>
              <p className="font-['JetBrains_Mono'] text-[#00F0FF] text-xs tracking-widest uppercase mb-2">Selected Work</p>
              <h2 className="font-['Syne'] text-2xl md:text-3xl font-bold text-white">Portfolio</h2>
            </div>
            <Link to="/portfolio" className="flex items-center gap-1 text-zinc-400 hover:text-[#D946EF] font-['Manrope'] text-sm transition-colors duration-200" data-testid="view-all-portfolio-link">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {categories.map((item, i) => (
              <Link
                key={item.slug}
                to={`/portfolio/${item.slug}`}
                className={`group relative aspect-[3/4] overflow-hidden rounded-lg hover-lift animate-fade-in-up stagger-${i + 1}`}
                style={{ borderBottom: '2px solid transparent', transition: 'border-color 0.3s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#D946EF'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                data-testid={`portfolio-preview-${item.slug}`}
              >
                <img src={mediaUrl(item.image)} alt={item.name} decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                {/* Watermark number */}
                <span className="absolute top-4 right-4 font-['Syne'] text-[5rem] md:text-[6rem] font-black text-white/[0.06] leading-none select-none pointer-events-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="absolute bottom-0 left-0 p-6">
                  <p className="font-['Syne'] text-lg font-bold text-white relative inline-block">
                    <span className="relative">
                      {item.name}
                      <span className="absolute bottom-0 left-0 w-0 h-px bg-[#D946EF] group-hover:w-full transition-all duration-300" />
                    </span>
                  </p>
                  <p className="font-['JetBrains_Mono'] text-xs text-zinc-400 mt-1 flex items-center gap-1">
                    Explore <ArrowRight className="w-3 h-3" />
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section Divider ─── */}
      <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(217,70,239,0.3), transparent)' }} />

      {/* ─── E-Book Showcase ─── */}
      {ebooks.length > 0 && (
        <section className="relative py-20 md:py-32 px-4 md:px-8 overflow-hidden" data-testid="ebook-showcase-section">
          <div className="absolute inset-0 bg-[#050505]" />
          {/* Diagonal magenta stripe */}
          <div className="absolute -top-20 -right-32 w-[60%] h-[200%] opacity-[0.07] rotate-12 blur-2xl" style={{ background: 'linear-gradient(180deg, #D946EF, transparent)' }} />
          <div className="absolute -bottom-20 -left-32 w-[40%] h-[140%] opacity-[0.05] -rotate-12 blur-2xl" style={{ background: 'linear-gradient(0deg, #8B5CF6, transparent)' }} />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(217,70,239,0.5) 1px, transparent 0)', backgroundSize: '32px 32px' }} />

          <div className="relative z-10 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-3 mb-2">
              <span className="h-[2px] w-10 bg-[#D946EF]" />
              <p className="font-['JetBrains_Mono'] text-[#D946EF] text-[10px] sm:text-xs tracking-[0.4em] uppercase font-bold inline-flex items-center gap-2">
                <Sparkles className="w-3 h-3 animate-pulse" /> Level Up
              </p>
              <span className="hidden sm:inline-block font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-zinc-600 uppercase">
                Vol. 01
              </span>
            </div>
            <div className="flex items-end justify-between gap-4 mb-6 md:mb-8 flex-wrap">
              <h2 className="font-['Syne'] text-4xl sm:text-5xl md:text-6xl font-black leading-[0.95] tracking-tight">
                <span className="text-white">Interactive </span>
                <span className="italic bg-gradient-to-r from-[#FFB6F4] via-[#D946EF] to-[#8B5CF6] bg-clip-text text-transparent">
                  E-Books.
                </span>
              </h2>
              <Link to="/ebooks" className="hidden sm:inline-block">
                <Button className="bg-[#D946EF] hover:bg-[#FFB6F4] text-black font-['Syne'] font-extrabold px-5 py-2.5 rounded-full text-xs sm:text-sm shadow-[0_0_24px_rgba(217,70,239,0.4)] hover:shadow-[0_0_32px_rgba(217,70,239,0.6)] transition-all" data-testid="ebook-browse-btn">
                  See all <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </Link>
            </div>
            <p className="text-zinc-400 font-['Manrope'] text-sm md:text-[15px] max-w-xl mb-8 -mt-4">
              Read it. <span className="text-[#FFB6F4] font-semibold">Quiz it.</span> Apply it. <span className="text-white font-semibold">90-day action plan included.</span>
            </p>

            <div className={`grid grid-cols-1 ${ebooks.length === 1 ? "max-w-3xl" : "sm:grid-cols-2"} gap-5`}>
              {ebooks.slice(0, 4).map((ebook, i) => {
                const chapterCount = ebook.chapters?.length || 0;
                const estimatedReadMin = Math.max(8, chapterCount * 4);
                return (
                  <Link
                    key={ebook.id}
                    to={user ? `/ebook/${ebook.id}` : `/auth?redirect=/ebook/${ebook.id}`}
                    className="group relative rounded-2xl overflow-hidden flex hover:-translate-y-1 transition-all duration-500 glass-card"
                    data-testid={`ebook-card-${ebook.id}`}
                  >
                    {/* Animated border gradient */}
                    <div className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      style={{ background: 'linear-gradient(135deg, #D946EF, #8B5CF6, #3B82F6)', WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)', WebkitMaskComposite: 'xor', padding: '1px' }} />
                    {/* Glow */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      style={{ background: 'radial-gradient(circle at 25% 50%, rgba(217,70,239,0.18), transparent 60%)' }} />

                    {/* Tilted book cover with page-stack effect */}
                    <div className="relative w-32 sm:w-36 flex-shrink-0 flex items-center justify-center py-6 pl-5">
                      <div className="relative w-full aspect-[3/4] transform -rotate-3 group-hover:-rotate-6 group-hover:scale-105 transition-transform duration-500"
                        style={{ filter: 'drop-shadow(0 12px 18px rgba(217,70,239,0.35))' }}>
                        {/* Page stack lines behind the book */}
                        <div className="absolute inset-0 rounded-md translate-x-[6px] translate-y-[6px] border border-white/[0.06] bg-transparent" />
                        <div className="absolute inset-0 rounded-md translate-x-[4px] translate-y-[4px] border border-white/[0.08] bg-transparent" />
                        <div className="absolute inset-0 rounded-md translate-x-[2px] translate-y-[2px] border border-white/[0.10] bg-[#0A0A0A]/40" />
                        {/* Actual book cover */}
                        <div className="absolute inset-0 rounded-md overflow-hidden ring-1 ring-white/10">
                          {ebook.cover_image ? (
                            <img
                              src={mediaUrl(ebook.cover_image)}
                              alt={ebook.title}
                              decoding="async"
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.querySelector('.ebook-cover-fallback').style.display = 'flex'; }}
                              onLoad={(e) => { if (e.target.naturalWidth < 10) { e.target.style.display = 'none'; e.target.parentElement.querySelector('.ebook-cover-fallback').style.display = 'flex'; } }}
                            />
                          ) : null}
                          <div className={`ebook-cover-fallback w-full h-full items-center justify-center relative ${ebook.cover_image ? 'hidden' : 'flex'}`}
                            style={{ background: 'linear-gradient(135deg, #D946EF 0%, #8B5CF6 50%, #3B82F6 100%)' }}>
                            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)', backgroundSize: '12px 12px' }} />
                            <div className="absolute inset-x-3 top-3 h-[1px] bg-white/40" />
                            <div className="absolute inset-x-3 bottom-3 h-[1px] bg-white/40" />
                            <div className="relative z-[1] text-center px-2">
                              <BookOpen className="w-7 h-7 text-white mx-auto mb-1.5" />
                              <p className="font-['Syne'] text-[11px] font-black text-white leading-tight line-clamp-3 uppercase tracking-wide">
                                {ebook.title}
                              </p>
                            </div>
                          </div>
                          {/* Spine highlight */}
                          <div className="absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-white/30 via-transparent to-white/20" />
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0 p-4 sm:p-5 pl-3 flex flex-col justify-between relative z-[1]">
                      <div>
                        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                          {i === 0 ? (
                            <span
                              className="font-['JetBrains_Mono'] text-[9px] tracking-[0.2em] uppercase font-extrabold text-white bg-[#D946EF] px-2 py-0.5 rounded-full"
                              style={{ animation: 'pulse-neon 2s ease-in-out infinite' }}
                            >
                              NEW
                            </span>
                          ) : null}
                          <span className="font-['JetBrains_Mono'] text-[9px] tracking-widest uppercase text-zinc-500 inline-flex items-center gap-1">
                            {chapterCount} ch · <span className="text-[#FFB6F4]">interactive</span>
                          </span>
                        </div>
                        <h3 className="font-['Syne'] text-lg sm:text-xl font-black text-white leading-[1.1] mb-2 line-clamp-2 group-hover:bg-gradient-to-r group-hover:from-[#FFB6F4] group-hover:to-[#D946EF] group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                          {ebook.title}
                        </h3>
                        <p className="font-['Manrope'] text-xs sm:text-[13px] text-zinc-400 line-clamp-2 leading-snug">
                          {ebook.description}
                        </p>
                      </div>

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.06]">
                        <div className="flex items-center gap-2">
                          {ebook.price > 0 ? (
                            <Price
                              usd={ebook.price_usd || ebook.price}
                              className="font-['Syne'] text-lg font-black text-white"
                            />
                          ) : (
                            <span className="font-['Syne'] text-base font-black text-emerald-400 inline-flex items-center gap-1">
                              <Sparkles className="w-3.5 h-3.5" /> FREE
                            </span>
                          )}
                          {(ebook.free_for_tiers?.length > 0 || ebook.free_for_paid_members) && ebook.price > 0 && (
                            <span className="flex items-center gap-1 font-['Manrope'] text-[9px] font-bold text-[#8B5CF6] bg-[#8B5CF6]/15 px-1.5 py-0.5 rounded-full">
                              <Crown className="w-2.5 h-2.5" /> Members
                            </span>
                          )}
                          <span className="font-['JetBrains_Mono'] text-[9px] text-zinc-600 hidden sm:inline">
                            ~{estimatedReadMin} min read
                          </span>
                        </div>
                        <span className="inline-flex items-center gap-1 font-['JetBrains_Mono'] text-[10px] font-bold uppercase tracking-widest text-[#D946EF] group-hover:gap-2 group-hover:text-[#FFB6F4] transition-all">
                          Read <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Mobile-only "See all" */}
            <div className="text-center mt-6 sm:hidden">
              <Link to="/ebooks">
                <Button className="bg-[#D946EF] hover:bg-[#FFB6F4] text-black font-['Syne'] font-extrabold px-6 py-2.5 rounded-full text-xs shadow-[0_0_24px_rgba(217,70,239,0.4)]">
                  See all e-books <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ─── Section Divider ─── */}
      <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(217,70,239,0.3), transparent)' }} />

      {/* ─── Travel Schedule ─── */}
      {travel.length > 0 && (
        <section className="relative overflow-hidden py-20 md:py-32 px-4 md:px-8" data-testid="travel-schedule-section">
          {/* World map dot pattern background */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none opacity-[0.035]"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(217,70,239,0.6) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          {/* Aurora glow */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(700px 280px at 85% 15%, rgba(217,70,239,0.16), transparent 60%), radial-gradient(560px 240px at 15% 85%, rgba(139,92,246,0.12), transparent 60%)",
            }}
          />

          <div className="relative max-w-7xl mx-auto">
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-4">
              <span className="block w-8 h-px bg-[#D946EF]" />
              <span className="font-['JetBrains_Mono'] text-[10px] tracking-[0.35em] uppercase text-[#D946EF]">
                Global Tour · {new Date().getFullYear()}
              </span>
            </div>

            <h2 className="font-['Syne'] font-black leading-[0.9] mb-4 md:mb-5">
              <span className="block text-3xl md:text-5xl xl:text-6xl text-white">On the</span>
              <span className="block text-4xl md:text-6xl xl:text-7xl italic mt-1">
                <span className="bg-gradient-to-r from-[#FFB6F4] via-[#D946EF] to-[#8B5CF6] bg-clip-text text-transparent">
                  move.
                </span>
              </span>
            </h2>

            <p className="font-['Libre_Baskerville'] italic text-zinc-400 text-sm md:text-base max-w-xl mb-8 md:mb-12">
              Pick a city. Lock your slot. Limited days, by appointment only.
            </p>

            {/* Itinerary list */}
            <ul className="border-t border-white/10">
              {travel.map((item, i) => {
                const fromD = new Date(item.date_from);
                const toD = new Date(item.date_to);
                const now = new Date();
                const isCurrentlyHere = now >= fromD && now <= toD;
                return (
                  <li key={item.id} className={`animate-fade-in-up stagger-${Math.min(i + 1, 5)}`}>
                    <button
                      onClick={() => navigate(`/booking?location=${encodeURIComponent(`${item.location}, ${item.country}`)}`)}
                      onMouseEnter={() => prefetchPackages()}
                      onTouchStart={() => prefetchPackages()}
                      className="group relative w-full block py-5 md:py-7 px-2 md:px-4 -mx-2 md:-mx-4 border-b border-white/10 text-left transition-all duration-500 hover:bg-gradient-to-r hover:from-[#D946EF]/[0.05] hover:via-transparent hover:to-transparent"
                      data-testid={`travel-item-${item.location.toLowerCase()}`}
                    >
                      {/* Gradient left border on hover (magenta to purple) */}
                      <span
                        className="absolute left-0 top-0 bottom-0 w-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{ background: 'linear-gradient(to bottom, #D946EF, #8B5CF6)' }}
                      />

                      <div className="grid grid-cols-12 gap-3 md:gap-6 items-end md:items-center">
                        {/* Index */}
                        <div className="col-span-2 md:col-span-1">
                          <span className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-zinc-600 group-hover:text-[#D946EF] transition-colors duration-300 flex items-center gap-1.5">
                            {String(i + 1).padStart(2, "0")}
                            {isCurrentlyHere && (
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                              </span>
                            )}
                          </span>
                        </div>

                        {/* City name */}
                        <div className="col-span-10 md:col-span-6">
                          <h3 className="font-['Syne'] font-black leading-[0.95] text-2xl sm:text-3xl md:text-4xl xl:text-5xl tracking-tight text-white transition-all duration-500 group-hover:translate-x-2">
                            <span className="bg-gradient-to-r from-white via-white to-white bg-clip-text text-transparent group-hover:from-[#FFB6F4] group-hover:via-[#D946EF] group-hover:to-[#8B5CF6] transition-all duration-500">
                              {item.location}
                            </span>
                            <span className="text-[#D946EF]">.</span>
                          </h3>
                          <div className="flex items-center gap-2 mt-1.5 md:mt-2">
                            <p className="font-['JetBrains_Mono'] text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                              {item.country}
                            </p>
                            {isCurrentlyHere && (
                              <span className="font-['JetBrains_Mono'] text-[8px] tracking-widest uppercase text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded-full">
                                Currently Here
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Elegant italic date — serif font */}
                        <div className="hidden md:block col-span-3">
                          <p className="font-['Libre_Baskerville'] italic text-zinc-200 text-sm lg:text-base leading-snug">
                            {fromD.toLocaleDateString("en-GB", { day: "numeric", month: "long" })}
                            <span className="text-[#D946EF] mx-2">→</span>
                            {toD.toLocaleDateString("en-GB", { day: "numeric", month: "long" })}
                          </p>
                          <p className="font-['JetBrains_Mono'] text-[9px] tracking-[0.3em] uppercase text-zinc-600 mt-1">
                            {toD.getFullYear()}
                          </p>
                        </div>

                        {/* CTA */}
                        <div className="col-span-12 md:col-span-2 flex md:justify-end">
                          <span className="inline-flex items-center gap-2 font-['JetBrains_Mono'] text-[10px] tracking-[0.25em] uppercase text-zinc-500 group-hover:text-[#D946EF] group-hover:gap-3 transition-all duration-300">
                            <span className="hidden md:inline">Book Slot</span>
                            <span className="md:hidden">Reserve</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>

                      {/* Mobile-only date row */}
                      <div className="md:hidden mt-2 pl-[calc(16.66%+0px)]">
                        <p className="font-['Libre_Baskerville'] italic text-zinc-300 text-xs">
                          {fromD.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                          <span className="text-[#D946EF] mx-1.5">→</span>
                          {toD.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Closing tag */}
            <div className="mt-8 md:mt-10 flex items-center gap-3">
              <span className="block w-8 h-px bg-zinc-700" />
              <span className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                Don't see your city? <button onClick={() => navigate("/contact-us")} className="text-[#D946EF] hover:text-[#FFB6F4] transition-colors underline-offset-4 hover:underline" data-testid="travel-request-city-btn">Request a visit →</button>
              </span>
            </div>
          </div>
        </section>
      )}

      {/* ─── Section Divider ─── */}
      <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(217,70,239,0.3), transparent)' }} />

      {/* ─── Community Promotion ─── */}
      {communityTiers.length > 0 && (
        <section className="relative py-20 md:py-32 px-4 md:px-8 overflow-hidden" data-testid="community-showcase-section">
          <div
            className="absolute inset-0 pointer-events-none opacity-60"
            style={{
              background:
                "radial-gradient(800px 280px at 15% 20%, rgba(217,70,239,0.08), transparent 60%), radial-gradient(700px 240px at 90% 80%, rgba(139,92,246,0.08), transparent 60%)",
            }}
          />

          <div className="relative z-10 max-w-6xl mx-auto">
            <div className="glass-card rounded-3xl p-6 md:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
                {/* Left — editorial headline */}
                <div>
                  <p className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] uppercase text-[#D946EF] mb-3 inline-flex items-center gap-2">
                    <Users className="w-3 h-3" /> Join the Movement
                  </p>
                  <h2 className="font-['Syne'] text-3xl md:text-4xl font-extrabold text-white leading-[1.05] mb-3">
                    The <span className="font-['Playfair_Display'] italic font-medium text-[#FFB6F4]">Propix3ls</span> Community
                  </h2>
                  <p className="font-['Libre_Baskerville'] italic text-sm md:text-[15px] text-zinc-400 leading-relaxed mb-6 max-w-md">
                    Level up your content game. Training, a creator network, and real brand deals — pick the tier that fits your hustle.
                  </p>
                  <Link
                    to="/community"
                    className="inline-flex items-center gap-2 px-5 h-11 rounded-full font-['Manrope'] text-sm font-bold text-black transition hover:-translate-y-0.5"
                    style={{ background: "linear-gradient(135deg, #FBBF24, #D946EF)", boxShadow: "0 10px 30px -10px rgba(217,70,239,0.45)" }}
                    data-testid="community-explore-link"
                  >
                    Explore the Community <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Right — compact tier rows */}
                <div className="space-y-2.5 min-w-0">
                  {communityTiers.map((tier) => {
                    const iconMap = { free: Zap, basic: Star, premium: Crown };
                    const colorMap = { free: "#10B981", basic: "#8B5CF6", premium: "#D946EF" };
                    const TIcon = iconMap[tier.slug] || Star;
                    const tColor = colorMap[tier.slug] || "#8B5CF6";
                    const isPremium = tier.slug === "premium";
                    const isExpanded = expandedTierId === tier.id;
                    const longDesc = tier.full_description || tier.description || "";
                    const features = Array.isArray(tier.features) ? tier.features.filter(Boolean) : [];
                    return (
                      <div
                        key={tier.id}
                        className="rounded-2xl border border-white/5 hover:border-white/15 bg-white/[0.015] hover:bg-white/[0.04] transition overflow-hidden relative"
                        data-testid={`community-tier-${tier.slug}`}
                      >
                        {/* Gradient accent strip on left */}
                        <span
                          className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full"
                          style={{ background: `linear-gradient(to bottom, ${tColor}, transparent)` }}
                        />

                        <div className="flex items-center gap-4 px-4 pl-5 py-3.5 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 relative overflow-hidden ${isPremium ? '' : ''}`}
                            style={{ background: `${tColor}18`, boxShadow: `inset 0 0 0 1px ${tColor}30` }}
                          >
                            {/* Shimmer animation on icon background */}
                            <div
                              className="absolute inset-0"
                              style={{
                                background: `linear-gradient(90deg, transparent, ${tColor}22, transparent)`,
                                backgroundSize: '200% 100%',
                                animation: 'shimmer 2.5s ease-in-out infinite',
                              }}
                            />
                            <TIcon className="w-4 h-4 relative z-[1]" style={{ color: tColor }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <h3 className="font-['Syne'] text-[15px] font-bold text-white">{tier.name}</h3>
                              {tier.is_free ? (
                                <span className="font-['JetBrains_Mono'] text-[10px] tracking-widest uppercase text-emerald-400">Free</span>
                              ) : (
                                <span className="font-['JetBrains_Mono'] text-[10px] tracking-widest uppercase" style={{ color: tColor }}>
                                  {tier.currency || "GBP"} {tier.price_monthly > 0 ? `${tier.price_monthly}/mo` : `${tier.price_onetime} once`}
                                </span>
                              )}
                            </div>
                            <p className={`font-['Manrope'] text-xs text-zinc-400 mt-1 leading-relaxed ${isExpanded ? "" : "line-clamp-1"}`}>
                              {tier.description}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setExpandedTierId(isExpanded ? null : tier.id)}
                            className="flex items-center gap-1 px-3 h-8 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 font-['JetBrains_Mono'] text-[10px] tracking-widest uppercase text-zinc-300 hover:text-white transition flex-shrink-0"
                            data-testid={`tier-more-btn-${tier.slug}`}
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? "Less" : "More"}
                            <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-300 ${isExpanded ? "rotate-90" : ""}`} />
                          </button>
                        </div>

                        {/* Premium gold shimmer overlay */}
                        {isPremium && (
                          <div
                            className="absolute inset-0 pointer-events-none rounded-2xl"
                            style={{
                              background: 'linear-gradient(90deg, transparent, rgba(251,191,36,0.04), transparent)',
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 3s ease-in-out infinite',
                            }}
                          />
                        )}

                        {isExpanded && (
                          <div
                            className="px-4 pb-4 pl-5 sm:pl-[4.75rem] border-t border-white/5 pt-3 animate-fade-in"
                            data-testid={`tier-expanded-${tier.slug}`}
                          >
                            <p className="font-['Manrope'] text-sm text-zinc-300 leading-relaxed mb-3 break-words">
                              {longDesc}
                            </p>
                            {features.length > 0 && (
                              <ul className="space-y-1.5 mb-4">
                                {features.slice(0, 6).map((f, idx) => (
                                  <li key={idx} className="flex items-start gap-2 font-['Manrope'] text-xs text-zinc-400">
                                    <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tColor }} />
                                    <span className="break-words">{f}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <Link
                              to="/community"
                              className="inline-flex items-center gap-1.5 font-['JetBrains_Mono'] text-[11px] tracking-widest uppercase hover:gap-2.5 transition-all"
                              style={{ color: tColor }}
                              data-testid={`tier-cta-${tier.slug}`}
                            >
                              Explore {tier.name} <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─── Section Divider ─── */}
      <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(217,70,239,0.3), transparent)' }} />

      {/* ─── CTA Section ─── */}
      <section className="py-20 md:py-32 px-4 md:px-8" data-testid="cta-section">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl gradient-border">
            {/* Floating abstract shapes */}
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-20 blur-[60px] pointer-events-none" style={{ background: '#D946EF' }} />
            <div className="absolute -bottom-12 left-1/3 w-36 h-36 rounded-full opacity-15 blur-[50px] pointer-events-none" style={{ background: '#8B5CF6' }} />
            <div className="absolute top-1/2 -right-8 w-24 h-24 rounded-full opacity-10 blur-[40px] pointer-events-none" style={{ background: '#00F0FF' }} />

            {/* Parallax-style oversize image */}
            <img
              src="https://images.unsplash.com/photo-1719362421977-402613bad637?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxpbmZsdWVuY2VyJTIwY3JlYXRpbmclMjBjb250ZW50JTIwcmluZyUyMGxpZ2h0JTIwY2FtZXJhJTIwc3R1ZGlvfGVufDB8fHx8MTc3MzYwMjg3OHww&ixlib=rb-4.1.0&q=85"
              alt="Creator collab"
              decoding="async"
              loading="lazy"
              className="w-full h-72 md:h-96 object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-8 md:px-12 max-w-lg">
                <p className="font-['JetBrains_Mono'] text-[#D946EF] text-xs tracking-widest uppercase mb-3">Creators Wanted</p>
                <h2 className="font-['Syne'] text-2xl md:text-3xl font-bold text-white mb-3">Want to Collaborate?</h2>
                <p className="text-zinc-400 font-['Manrope'] text-sm mb-6">
                  Selected creators receive a free photoshoot and video reels. Apply now.
                </p>
                <Link to="/collaborate">
                  <Button className="bg-[#D946EF] hover:bg-[#C026D3] text-white font-['Manrope'] font-semibold px-6 py-3 rounded-full text-sm" data-testid="cta-collaborate-btn">
                    Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
